// Pure chat timeline helpers (T-306d): day separators, sender groups, list times, quick statuses.
import type { ChatMessage, ChatStatus } from '@spot/shared';

/** Messages from one sender within this gap share one group (one avatar + name). */
export const GROUP_GAP_MS = 5 * 60_000;
const DAY_MS = 86_400_000;

export type TimelineItem =
	| { type: 'day'; key: string; date: Date }
	| { type: 'message'; message: ChatMessage; first: boolean; last: boolean };

/** Calendar day in the city timezone, e.g. "2026-10-21". */
export function dayKey(date: Date, timeZone: string): string {
	return new Intl.DateTimeFormat('en-CA', {
		timeZone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	}).format(date);
}

/** Whole days from `date` to `now` in the city timezone (0 = today, 1 = yesterday). */
export function daysAgo(date: Date, now: Date, timeZone: string): number {
	const at = (d: Date) => Date.parse(`${dayKey(d, timeZone)}T00:00:00Z`);
	return Math.round((at(now) - at(date)) / DAY_MS);
}

function groupKey(message: ChatMessage): string | null {
	return message.system !== null ? null : (message.sender?.id ?? (message.mine ? 'me' : null));
}

/** Day separators + `first`/`last` flags for consecutive messages of one sender. */
export function buildTimeline(messages: readonly ChatMessage[], timeZone: string): TimelineItem[] {
	const items: TimelineItem[] = [];
	let lastDay: string | null = null;
	messages.forEach((message, i) => {
		const date = new Date(message.createdAt);
		const day = dayKey(date, timeZone);
		if (day !== lastDay) {
			items.push({ type: 'day', key: day, date });
			lastDay = day;
		}
		const joins = (a: ChatMessage | undefined, b: ChatMessage | undefined) =>
			a !== undefined &&
			b !== undefined &&
			groupKey(a) !== null &&
			groupKey(a) === groupKey(b) &&
			dayKey(new Date(a.createdAt), timeZone) === dayKey(new Date(b.createdAt), timeZone) &&
			Math.abs(Date.parse(b.createdAt) - Date.parse(a.createdAt)) <= GROUP_GAP_MS;
		items.push({
			type: 'message',
			message,
			first: !joins(messages[i - 1], message),
			last: !joins(message, messages[i + 1]),
		});
	});
	return items;
}

/** Each person's latest quick status among the loaded messages (a later text doesn't clear it). */
export function latestStatuses(messages: readonly ChatMessage[]): Map<string, ChatStatus> {
	const statuses = new Map<string, ChatStatus>();
	for (const message of messages) {
		if (message.sender === null) continue;
		if (message.kind === 'status_arrived') statuses.set(message.sender.id, 'arrived');
		if (message.kind === 'status_late') statuses.set(message.sender.id, 'late');
	}
	return statuses;
}

type Translate = (key: 'chat.today' | 'chat.yesterday') => string;

/** Day separator: «Сьогодні», «Вчора», else «пт, 17 жовтня». */
export function dayLabel(
	date: Date,
	now: Date,
	timeZone: string,
	locale: string,
	t: Translate,
): string {
	const ago = daysAgo(date, now, timeZone);
	if (ago === 0) return t('chat.today');
	if (ago === 1) return t('chat.yesterday');
	return new Intl.DateTimeFormat(locale, {
		timeZone,
		weekday: 'short',
		day: 'numeric',
		month: 'long',
	}).format(date);
}

/** Chats list time: «14:05» today, «Вчора», a weekday within a week, else «17.10». */
export function listTime(
	date: Date,
	now: Date,
	timeZone: string,
	locale: string,
	t: Translate,
): string {
	const ago = daysAgo(date, now, timeZone);
	if (ago <= 0) {
		return new Intl.DateTimeFormat(locale, {
			timeZone,
			hour: '2-digit',
			minute: '2-digit',
			hourCycle: 'h23',
		}).format(date);
	}
	if (ago === 1) return t('chat.yesterday');
	if (ago < 7) return new Intl.DateTimeFormat(locale, { timeZone, weekday: 'short' }).format(date);
	return new Intl.DateTimeFormat(locale, { timeZone, day: '2-digit', month: '2-digit' }).format(
		date,
	);
}
